import { Controller } from "@hotwired/stimulus"
import { icone } from "../lib/icones"

export default class extends Controller {
    static targets = [
        "step1Option", "cursor",
        "step2Input", "step2Preview",
        "step3List", "step3Total"
    ]

    connect() {
        this.startStep1Animation()
        this.startStep2Animation()
        this.startStep3Animation()
    }

    // ==========================================
    // STEP 1: Cursor Selection Animation
    // ==========================================
    async startStep1Animation() {
        if (!this.hasCursorTarget || this.step1OptionTargets.length === 0) return

        let index = 0
        while (true) {
            const target = this.step1OptionTargets[index]

            // Move cursor
            await this.moveCursorTo(target)

            // Click effect
            this.cursorTarget.classList.add("scale-90")
            target.classList.add("ring-2", "ring-indigo-500", "bg-indigo-50")
            await this.wait(200)
            this.cursorTarget.classList.remove("scale-90")

            await this.wait(1500)

            // Reset
            target.classList.remove("ring-2", "ring-indigo-500", "bg-indigo-50")
            index = (index + 1) % this.step1OptionTargets.length
        }
    }

    moveCursorTo(element) {
        return new Promise(resolve => {
            // Get relative position within the container
            const containerRect = this.element.getBoundingClientRect()
            const rect = element.getBoundingClientRect()

            // Calculate center position relative to the controller container
            // Note: We assume the cursor is absolute positioned within the same relative container
            // We need to account for the container's scroll/position if strictly needed, 
            // but usually taking the difference in clientRects works for fixed viewports.
            // Better approach for nested elements:
            // x = el.offsetLeft + width/2 (if direct parent) - simplified logic below:

            if (!this.hasCursorTarget || !this.cursorTarget.offsetParent) {
                resolve()
                return
            }

            const parentRect = this.cursorTarget.offsetParent.getBoundingClientRect()

            const top = rect.top - parentRect.top + rect.height / 2 + 10 // +10 is offset for "hand" visual
            const left = rect.left - parentRect.left + rect.width / 2 + 10

            this.cursorTarget.style.transform = `translate(${left}px, ${top}px)`

            setTimeout(resolve, 800) // Transition duration matching CSS
        })
    }

    // ==========================================
    // STEP 2: Builder Typing Animation
    // ==========================================
    async startStep2Animation() {
        if (!this.hasStep2InputTarget || !this.hasStep2PreviewTarget) return

        const fullTitle = "Masterclass: Vente & Persuasion"

        // Set up the preview element with a ghost span (invisible, reserves space)
        // and a typed span (visible, fills in progressively)
        this.step2PreviewTarget.innerHTML = `
            <span class="relative inline-block w-full">
                <span class="invisible">${fullTitle}</span>
                <span data-typed class="absolute inset-0 text-left"></span>
            </span>
        `
        const typedSpan = this.step2PreviewTarget.querySelector('[data-typed]')

        while (true) {
            // Clear
            this.step2InputTarget.value = ""
            typedSpan.textContent = "..."
            await this.wait(1000)

            // Type forward
            for (let i = 0; i <= fullTitle.length; i++) {
                const text = fullTitle.substring(0, i)
                this.step2InputTarget.value = text
                typedSpan.textContent = text || "..."
                await this.wait(50 + Math.random() * 50)
            }

            await this.wait(3000)
        }
    }

    // ==========================================
    // STEP 3: Falling Transactions
    // ==========================================
    startStep3Animation() {
        if (!this.hasStep3ListTarget) return

        const names = ["Amel T.", "Karim B.", "Youssef S.", "Nour E.", "Mehdi J.", "Sarra L."]
        const products = ["Formation Vente", "Ebook Strategy", "Coaching 1h", "Pack Premium"]

        setInterval(() => {
            this.addTransaction(
                names[Math.floor(Math.random() * names.length)],
                products[Math.floor(Math.random() * products.length)],
                Math.floor(Math.random() * 200) + 40
            )
        }, 2500)
    }

    addTransaction(name, product, amount) {
        const row = document.createElement("div")
        row.className = "flex items-center justify-between p-4 bg-white rounded-2xl border border-slate-100 shadow-sm transition-all duration-700 transform translate-y-[-20px] opacity-0 mb-4"
        row.innerHTML = `
      <div class="flex items-center gap-4">
         <div class="w-10 h-10 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center text-xs shadow-inner">${icone("arrow-down")}</div>
         <div class="text-left">
            <div class="text-sm font-bold text-slate-900 tracking-tight">${name}</div>
            <div class="text-[10px] text-slate-400">${product}</div>
         </div>
      </div>
      <div class="font-bold text-emerald-600 text-base">+${amount} TND</div>
    `

        // Prepend
        this.step3ListTarget.prepend(row)

        // Animate in
        requestAnimationFrame(() => {
            row.classList.remove("translate-y-[-20px]", "opacity-0")
        })

        // Update total visual (optional fake increment)
        if (this.hasStep3TotalTarget) {
            let currentInfo = this.step3TotalTarget.innerText.split(' ')
            let currentVal = parseInt(currentInfo[0].replace(',', '')) || 2850
            this.step3TotalTarget.innerHTML = `${currentVal + amount} <span class="text-lg text-slate-400 font-medium tracking-normal">TND</span>`
        }

        // Cleanup old items to prevent infinite DOM growth
        if (this.step3ListTarget.children.length > 5) {
            const last = this.step3ListTarget.lastElementChild
            last.style.opacity = '0'
            setTimeout(() => last.remove(), 500)
        }
    }

    wait(ms) {
        return new Promise(resolve => setTimeout(resolve, ms))
    }
}
